import {
  activityDetail,
  taskDetail,
  taskCheckRes,
  subTaskListItem,
  rewardClaimRes,
  ShortLinkRes,
  TaskListRes,
  activityDetailRes,
  walletChains,
  SubTaskType,
} from '@/types';
import { TokenBalance } from '@/types/user';
import {
  TaskListParams,
  getActivityDetail,
  getTaskList,
  activityApply,
  reportTask,
  taskCheck,
  saveSocialMedia,
  rewardClaim,
  socialMediaParams,
  getShortLink,
  getActionsMetadata,
  ActionsRes,
} from '@/services';
import {
  BalanceParams,
  BatchQueryParams,
  BatchQueryAddressDetail,
  BatchQueryResult,
  getCloudAssetBalanceList,
  getExtensionAssetBalanceList,
} from '@/services/user';
import {
  Chain,
  queryChainList,
  safeParseJSON,
  getCurrentLocale,
  WalletType,
} from '@web3/base-utils';
import { create } from 'zustand';
import { ethAddress } from '@/config/common';
// import { chaninId } from '@/utils/constant';
import { RequestWalletInfo } from '@/types/requestWalletInfo';

interface StoreType {
  shareObj: any;
  activityDetail?: activityDetail | null;
  taskList?: taskDetail[];
  depositTaskList?: taskDetail[];
  taskApplyObject: subTaskListItem | null;
  taskCheckResult: taskCheckRes | null;
  rewardClaimResult: rewardClaimRes | null;
  depositChainBalanceCloud: TokenBalance[] | TokenBalance;
  depositChainBalanceEX: BatchQueryAddressDetail[];
  swapChainBalance: TokenBalance[] | TokenBalance | BatchQueryResult;
  pcSupport?: boolean;
  shortLinkObj: ShortLinkRes | null;
  mnemonicChains: Chain[];
  actionsMetadata: ActionsRes | null;
  setShareObj: (data: any) => void;
  fetchActivityDetail: (data?: {
    activityId: number;
    chainId: number;
    walletAddress?: string;
    walletType?: WalletType;
    walletId?: string;
  }) => Promise<activityDetailRes | undefined>;
  fetchDepositTaskList: (
    data?: TaskListParams
    // NOTE wallet info
  ) => Promise<TaskListRes | undefined>;
  fetchTaskList: (
    data?: TaskListParams
    // NOTE wallet info
  ) => Promise<TaskListRes | undefined>;
  doAtivityApply: (
    data: {
      taskId?: number;
      chainId?: number;
      walletAddress?: string;
      walletType?: WalletType;
      walletId?: string;
    },
    // NOTE wallet info
    walletInfo?: RequestWalletInfo
  ) => Promise<any | undefined>;
  doTaskReport: (data: {
    taskId?: number;
    chainId?: number;
    walletId?: string;
    walletType: WalletType;
    walletAddress?: string;
  }) => Promise<any | undefined>;
  doTaskCheck: (data: {
    taskId?: number;
    chainId?: number;
    walletId?: string;
    walletAddress?: string;
    walletType?: WalletType;
  }) => Promise<void>;
  setTaskApply: (data: subTaskListItem) => void;
  doSaveSocialMedia: (data: socialMediaParams) => Promise<void>;
  doRewardClaim: (data: {
    recordId: number | undefined;
    rewardId?: number | undefined;
    chainId?: number;
    walletId?: string;
    walletAddress?: string;
    walletType: WalletType;
  }) => Promise<any>;
  fetchDepositChainBalanceCloud: (data: BalanceParams) => Promise<void>;
  fetchDepositChainBalanceEX: (data: BatchQueryParams) => Promise<void>;
  fetchSwapChainBalanceCloud: (data: BalanceParams) => Promise<void>;
  fetchSwapChainBalanceEX: (data: BatchQueryParams) => Promise<void>;
  fetchShortLink: (data: {
    originalUrl: string;
    publicNetwork?: number;
  }) => Promise<ShortLinkRes | undefined>;
  fetchChainList: () => Promise<{ walletChains: walletChains } | undefined>;
  taskButtonUrl: (subTask: subTaskListItem) => string;
  fetchActions: (data: {
    actionId: number;
    iconIndex?: number;
  }) => Promise<ActionsRes | undefined>;
  // init: () => Promise<void>;
}

export const useActivityStore = create<StoreType>()(
  (
    set
    // get
  ) => ({
    shareObj: null,
    activityDetail: undefined,
    taskList: [],
    depositTaskList: [],
    taskCheckResult: null,
    taskApplyObject: null,
    rewardClaimResult: null,
    depositChainBalanceCloud: [],
    depositChainBalanceEX: [],
    swapChainBalance: [],
    shortLinkObj: null,
    mnemonicChains: [],
    actionsMetadata: null,
    pcSupport: true,
    setShareObj: (share: any) => {
      set(() => ({
        shareObj: share,
      }));
    },
    fetchActivityDetail: async (data) => {
      try {
        const res = await getActivityDetail(data);
        if (res.success) {
          set(
            () =>
              ({
                activityDetail: res.result || {},
                pcSupport:
                  res.result.activityInfo.clientConfig?.pcConfig.support,
              }) as any
          );
        } else {
          set(() => ({
            activityDetail: null,
          }));
        }
        return res;
        // set(() => ({ info: res.data }));
      } catch (e) {
        console.error(e);
      }
    },
    fetchDepositTaskList: async (data?: TaskListParams) => {
      try {
        const res = await getTaskList(data);
        if (res.success) {
          set(() => ({
            depositTaskList: res.result || [],
          }));
        } else {
          set(() => ({
            depositTaskList: [],
          }));
        }
        return res;
      } catch (e) {
        console.error(e);
      }
    },
    fetchTaskList: async (data?: TaskListParams) => {
      try {
        const res = await getTaskList(data);
        if (res.success) {
          set(() => ({
            taskList: res.result || [],
          }));
        } else {
          set(() => ({
            taskList: [],
          }));
        }
        return res;
      } catch (e) {
        console.error(e);
      }
    },
    doAtivityApply: async (data: {
      taskId?: number;
      chainId: number;
      walletAddress: string;
      walletType: WalletType;
      walletId: string;
    }) => {
      try {
        const res = await activityApply(data);
        if (res.success) {
          set(() => ({}));
        }
        return res;
      } catch (e) {
        console.error(e);
      }
    },
    doTaskReport: async (data) => {
      try {
        const res = await reportTask(data);
        if (res.success) {
          set(() => ({}));
        }
        return res;
      } catch (e) {
        console.error(e);
      }
    },
    doTaskCheck: async (data) => {
      try {
        const res = await taskCheck(data);
        if (res.success) {
          set(() => ({
            taskCheckResult: res.result,
          }));
        }
      } catch (e) {
        console.error(e);
      }
    },
    setTaskApply: (data: subTaskListItem) => {
      set(() => ({
        taskApplyObject: data,
      }));
    },
    doSaveSocialMedia: async (data: socialMediaParams) => {
      try {
        const res = await saveSocialMedia(data);
        if (res.success) {
          set(() => ({
            // taskCheckResult: res.result
          }));
        }
      } catch (e) {
        console.error(e);
      }
    },
    // 抽奖
    doRewardClaim: async (data: {
      recordId: number | undefined;
      rewardId?: number | undefined;
      chainId?: number;
      walletId?: string;
      walletType: WalletType;
      walletAddress: string;
    }) => {
      try {
        const res = await rewardClaim(data);
        set(() => ({
          rewardClaimResult: res.result,
        }));
        return res;
      } catch (e) {
        console.error(e);
      }
      return {};
    },
    // 托管钱包获取 充值 资产
    fetchDepositChainBalanceCloud: async (data: BalanceParams) => {
      try {
        const res = await getCloudAssetBalanceList(data);
        if (res.success) {
          set(() => ({
            depositChainBalanceCloud: res.result || [],
          }));
        }
      } catch (e) {
        console.error(e);
      }
    },
    // 插件钱包获取 充值 资产
    fetchDepositChainBalanceEX: async (data: BatchQueryParams) => {
      try {
        const res = await getExtensionAssetBalanceList(data);
        if (res.success) {
          set(() => ({
            depositChainBalanceEX: res.result.addressDetail || [],
          }));
        }
      } catch (e) {
        console.error(e);
      }
    },
    // 托管钱包获取 swap 资产
    fetchSwapChainBalanceCloud: async (data: BalanceParams) => {
      try {
        const res = await getCloudAssetBalanceList(data);
        if (res.success) {
          set(() => ({
            swapChainBalance: res.result || [],
          }));
        }
      } catch (e) {
        console.error(e);
      }
    },
    // 插件钱包获取 swap 资产
    fetchSwapChainBalanceEX: async (data: BatchQueryParams) => {
      try {
        const res = await getExtensionAssetBalanceList(data);
        if (res.success) {
          set(() => ({
            swapChainBalance: res.result || [],
          }));
        }
      } catch (e) {
        console.error(e);
      }
    },
    fetchShortLink: async (data: { originalUrl: string }) => {
      try {
        const res = await getShortLink(data);
        if (res.success) {
          set(
            () =>
              ({
                shortLinkObj: res.result || {},
              }) as any
          );
          return res.result;
        }
      } catch (e) {
        console.error(e);
      }
    },
    fetchChainList: async () => {
      try {
        const { success, result } = await queryChainList();
        const mnemonicChains =
          result?.walletChains?.MNEMONIC_PHRASE?.itemList || [];
        if (success) {
          set(() => ({
            mnemonicChains: mnemonicChains,
          }));
          return result;
        }
      } catch (e) {
        console.error(e);
      }
    },
    fetchActions: async (data: { actionId: number; iconIndex?: number }) => {
      try {
        const { success, result } = await getActionsMetadata(data);
        if (success) {
          set(() => ({
            actionsMetadata: result,
          }));
          return result;
        }
      } catch (e) {
        console.error(e);
      }
    },
    taskButtonUrl: (subTask: subTaskListItem) => {
      const origin = window.top?.location.origin || '';
      let url = subTask.webGoUrl;
      if (!origin.includes('-test-')) {
        /* eslint-disable bybit/no-static-domain */
        url = url
          .replace('https://www.bybit.com', origin)
          .replace('https://testnet.bybit.com', origin);
      }
      const taskRuleConfig = safeParseJSON(subTask.taskRuleConfig, {} as any);
      switch (subTask.taskType) {
        case SubTaskType.swap:
          url = `${origin}/${getCurrentLocale()}/web3/swap-bridge#toChain=${taskRuleConfig?.chainId}&toToken=${taskRuleConfig?.tokenSymbol}`;
          break;
        case SubTaskType.dexPro:
          url = `${origin}/${getCurrentLocale()}/web3/dex/${taskRuleConfig?.toChainCode}${taskRuleConfig?.toTokenAddress === ethAddress ? '' : `/${taskRuleConfig?.toTokenAddress}`}`;
          break;
      }
      return url;
    },
  })
);

// export default useStore;
